/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React from 'react';
import { useExpressionNameLookup } from '../../../hooks/useExpressionLibrary';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  createListCollection,
  Flex,
  HStack,
  Input,
  Select,
  Text,
  Textarea,
  VStack,
  Portal,
} from '@chakra-ui/react';
import { MappingAutocompleteInput, type MappingSuggestion } from '../inputs/MappingAutocompleteInput';
import {
  createPixelTemplateRow,
  createExtractionRow,
  createPixelRow,
  dtoCustomPixelsToRows,
  rowsToDtoCustomPixels,
  validatePixels,
  PIXEL_TEMPLATE_DEFINITIONS,
  type PixelTemplateId,
  type PixelExtractionRow,
  type PixelRow,
} from './pixel-editor.adapters';
import { rowAnchorId, useFindingReview, useRowOpenRequest, type RowOpenRequest } from './row-open-request';
import { ValidationSummary } from './ValidationSummary';
import type { ReusablePixel } from '../../../types';
import {
  type MacroExpression,
  expressionTypeBadgeColor,
  expressionTypeLabel,
} from '../../../types/decorator-expression';
import { DEFAULT_PROXY_PIXEL_URL_TEMPLATE, resolveProxyBaseUrlPlaceholder } from './pixel-template-snippet';
import { DecoratorExpressionEditor, ExpressionBadge, summarizeMacroExpression } from './DecoratorExpressionEditor';
import { inferValueTypeFromExpression, inferDefaultValueFromExpression } from './pixel-editor.inference';
import { isSelectable } from '../../../utils/admin-metadata';
import type { SavedLibraryEntry } from './DecoratorExpressionEditor';

interface PixelEditorProps {
  rows: PixelRow[];
  onChange: (rows: PixelRow[]) => void;
  mode?: 'multi' | 'single';
  showTemplateButton?: boolean;
  sharedPixels?: ReusablePixel[];
  proxyBaseUrl?: string;
  /** Shared expressions from the library, shown in the "Apply from library" picker. */
  sharedExpressions?: import('../../../types/expression-library').SharedExpression[];
  /**
   * Called when the user saves an extraction expression to the shared library.
   * Receives the chosen name, description, and the expression payload.
   */
  onSaveExpressionToLibrary?: (name: string, description: string, expr: MacroExpression) => Promise<SavedLibraryEntry>;
  /**
   * Called to save a demand-source pixel to the shared library (create a new
   * reusable pixel). Receives the pixel as a reusable DTO. When omitted, the
   * "Save to library" action is hidden.
   */
  onSavePixelToLibrary?: (pixel: Partial<ReusablePixel>) => Promise<void>;
  /**
   * **The row a finding asked this editor to open or reveal** (`#1305`), or `null`.
   *
   * The sixth editor, and it is in this set for the reason a read is a path: a pixel finding is
   * raised by `validatePixels`, carried on the panel under the response-mapping section, and lands
   * in this editor. Counting only the five that key rows the same way would have left the one
   * surface whose findings nobody could be pointed at.
   */
  openRow: RowOpenRequest | null;
}

const pixelTypeCollection = createListCollection({
  items: [
    { label: 'Impression', value: 'impression' },
    { label: 'Tracking', value: 'tracking' },
    { label: 'Custom', value: 'custom' },
  ],
});

const valueTypeCollection = createListCollection({
  items: [
    { label: 'String', value: 'string' },
    { label: 'Number', value: 'number' },
    { label: 'Integer', value: 'integer' },
    { label: 'Boolean', value: 'boolean' },
  ],
});

const transformCollection = createListCollection({
  items: [
    { label: 'None', value: '' },
    { label: 'Lowercase', value: 'lowercase' },
    { label: 'Uppercase', value: 'uppercase' },
    { label: 'Trim', value: 'trim' },
  ],
});

const pixelTemplateCollection = createListCollection({
  items: [
    { label: 'Custom (Blank Pixel)', value: 'custom' },
    ...PIXEL_TEMPLATE_DEFINITIONS.map((template) => ({
      label: template.label,
      value: template.id,
    })),
  ],
});

// ---------------------------------------------------------------------------
// Pixel field mapping autocomplete helpers
// ---------------------------------------------------------------------------

/**
 * **The fields a pixel extraction writes onto the ad** — `PIXEL_EXTRACTION_TARGETS` in
 * `libs/drivers/pixel-extraction-targets.ts`, which `pixel-extraction-targets.spec.ts` holds this list
 * to. `confidence`, `viewability` and `errorCode` were offered and reached nothing (`#1320` item 6).
 */
const STANDARD_PIXEL_FIELD_SUGGESTIONS: MappingSuggestion[] = [
  { value: 'bid', label: 'bid', description: 'Bid price / CPM value', category: 'standard' },
  { value: 'duration', label: 'duration', description: 'Ad duration in seconds', category: 'standard' },
  { value: 'adId', label: 'adId', description: 'Ad identifier', category: 'standard' },
  { value: 'creativeId', label: 'creativeId', description: 'Creative identifier', category: 'standard' },
  { value: 'advertiserDomain', label: 'advertiserDomain', description: 'Advertiser domain', category: 'standard' },
  { value: 'dealId', label: 'dealId', description: 'Deal identifier', category: 'standard' },
  { value: 'iabCategory', label: 'iabCategory', description: 'IAB content category', category: 'standard' },
];

/** URL macro params that are infrastructure-level – skip them from extraction suggestions. */
const SKIP_URL_MACRO_PARAMS = new Set([
  'sessionId',
  'podId',
  'podIndex',
  'cb',
  'ip',
  'ua',
  'gdpr',
  'gdpr_consent',
  'us_privacy',
  'dnt',
  'lmt',
  'w',
  'h',
  'url',
  'dur',
]);

/**
 * Build autocomplete suggestions for an extraction field name input.
 * Combines params found in the pixel URL template with the well-known standard list.
 */
function buildExtractionFieldSuggestions(urlTemplate: string): MappingSuggestion[] {
  const urlMatches = [...urlTemplate.matchAll(/\{([^{}]+)\}/g)];
  const urlParams = [...new Set(urlMatches.map((m) => m[1]))].filter((p) => !SKIP_URL_MACRO_PARAMS.has(p));
  const fromUrl: MappingSuggestion[] = urlParams.map((p) => ({
    value: p,
    label: p,
    description: 'Parameter in URL template',
    category: 'from-url',
  }));
  const seen = new Set<string>(urlParams);
  const standard = STANDARD_PIXEL_FIELD_SUGGESTIONS.filter((s) => !seen.has(s.value));
  return [...fromUrl, ...standard];
}

const adServerTemplateCollection = createListCollection({
  items: [
    { label: 'Generic', value: 'Generic' },
    { label: 'SpringServe', value: 'SpringServe' },
    { label: 'Magnite', value: 'Magnite' },
    { label: 'PubMatic', value: 'PubMatic' },
    { label: 'Prebid', value: 'Prebid' },
    { label: 'Criteo', value: 'Criteo' },
    { label: 'Google Ad Manager', value: 'Google Ad Manager' },
    { label: 'FreeWheel', value: 'FreeWheel' },
    { label: 'Xandr', value: 'Xandr' },
    { label: 'Equativ', value: 'Equativ' },
    { label: 'Smart AdServer', value: 'Smart AdServer' },
  ],
});

export const PixelEditor: React.FC<PixelEditorProps> = ({
  rows,
  onChange,
  mode = 'multi',
  showTemplateButton = true,
  sharedPixels,
  proxyBaseUrl,
  sharedExpressions,
  onSaveExpressionToLibrary,
  onSavePixelToLibrary,
  openRow,
}) => {
  // The library once, above the rows — see `useExpressionNameLookup`. Without it a row that
  // addresses a shared expression reads *shared expression* while the row next to it on
  // another surface names one, which is the drift a shared summary exists to prevent.
  const expressionNameFor = useExpressionNameLookup();
  const isSingleMode = mode === 'single';
  const issues = React.useMemo(() => validatePixels(rows), [rows]);
  const [collapsedRows, setCollapsedRows] = React.useState<Record<string, boolean>>({});

  /**
   * **How this editor answers _which row is that_** — a pixel is named, and `validatePixels` labels
   * a nameless one `pixel N` by its position, so the fallback here is the same expression rather
   * than a second one. A pixel card is expanded by default and *opening* it means un-collapsing,
   * which is why this editor's `open` is a removal from a collapse map rather than a selection.
   */
  const resolvePixelRow = (ref: string): string | null => {
    const wanted = ref.trim();
    const idx = rows.findIndex((r, i) => (r.name?.trim() || `pixel ${i + 1}`) === wanted);
    return idx === -1 ? null : rows[idx].id;
  };
  const expandPixelRow = (id: string) => setCollapsedRows((prev) => (prev[id] ? { ...prev, [id]: false } : prev));
  const findingReview = useFindingReview(
    'pixels',
    resolvePixelRow,
    (ref) => resolvePixelRow(ref) !== null,
    expandPixelRow,
  );
  useRowOpenRequest<string>('pixels', openRow, resolvePixelRow, expandPixelRow);
  /** Per-row save-to-library UI state. */
  const [savingPixelId, setSavingPixelId] = React.useState<string | null>(null);
  const [savedPixelId, setSavedPixelId] = React.useState<string | null>(null);
  const [savePixelError, setSavePixelError] = React.useState<{ id: string; message: string } | null>(null);
  /** Tracks which extraction row has its expression panel expanded: "pixelId:extId" */
  const [expandedExprKey, setExpandedExprKey] = React.useState<string | null>(null);
  /** Tracks which extraction rows are in collapsed summary view: "pixelId:extId" */
  const [collapsedExtractions, setCollapsedExtractions] = React.useState<Record<string, boolean>>({});
  const resolvedRows = React.useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        urlTemplate: resolveProxyBaseUrlPlaceholder(row.urlTemplate, proxyBaseUrl),
      })),
    [rows, proxyBaseUrl],
  );
  const sourceRowsById = React.useMemo(() => new Map(rows.map((row) => [row.id, row])), [rows]);
  const workingRows = isSingleMode
    ? resolvedRows.length > 0
      ? [resolvedRows[0]]
      : [
          createPixelRow({
            id: 'pixel-0',
            urlTemplate: resolveProxyBaseUrlPlaceholder(DEFAULT_PROXY_PIXEL_URL_TEMPLATE, proxyBaseUrl),
          }),
        ]
    : resolvedRows;

  const toggleCollapsed = (pixelId: string) => {
    setCollapsedRows((prev) => ({
      ...prev,
      [pixelId]: !prev[pixelId],
    }));
  };

  const updatePixel = (id: string, patch: Partial<PixelRow>) => {
    if (isSingleMode) {
      const row = workingRows[0];
      onChange([{ ...row, ...patch }]);
      return;
    }

    onChange(workingRows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removePixel = (id: string) => {
    onChange(workingRows.filter((row) => row.id !== id));
  };

  const addPixel = () => {
    if (isSingleMode) {
      onChange([
        createPixelRow({
          id: 'pixel-0',
          urlTemplate: resolveProxyBaseUrlPlaceholder(DEFAULT_PROXY_PIXEL_URL_TEMPLATE, proxyBaseUrl),
        }),
      ]);
      return;
    }

    onChange([
      ...workingRows,
      createPixelRow({
        urlTemplate: resolveProxyBaseUrlPlaceholder(DEFAULT_PROXY_PIXEL_URL_TEMPLATE, proxyBaseUrl),
      }),
    ]);
  };

  const addFromSelection = (selection: PixelTemplateId | 'custom') => {
    if (selection === 'custom') {
      addPixel();
      return;
    }

    if (isSingleMode) {
      onChange([
        createPixelTemplateRow(selection, {
          id: 'pixel-0',
          urlTemplate: resolveProxyBaseUrlPlaceholder(createPixelTemplateRow(selection).urlTemplate, proxyBaseUrl),
        }),
      ]);
      return;
    }

    const next = createPixelTemplateRow(selection);
    onChange([
      ...workingRows,
      {
        ...next,
        urlTemplate: resolveProxyBaseUrlPlaceholder(next.urlTemplate, proxyBaseUrl),
      },
    ]);
  };

  const availableSharedPixels = React.useMemo(
    () => (sharedPixels ?? []).filter((pixel) => isSelectable(pixel)),
    [sharedPixels],
  );

  const sharedPixelRows = React.useMemo(() => dtoCustomPixelsToRows(availableSharedPixels), [availableSharedPixels]);

  const sharedPixelCollection = React.useMemo(
    () =>
      createListCollection({
        items: sharedPixelRows.map((pixel, index) => ({
          label: `${pixel.name || `Shared Pixel ${index + 1}`} (${pixel.type || 'impression'})`,
          value: pixel.id,
        })),
      }),
    [sharedPixelRows],
  );

  const availableSharedExpressions = React.useMemo(
    () => (sharedExpressions ?? []).filter((e) => isSelectable(e)),
    [sharedExpressions],
  );

  const sharedExpressionCollection = React.useMemo(
    () =>
      createListCollection({
        items: availableSharedExpressions.map((e) => ({
          label: `${e.name}${e.description ? ` – ${e.description}` : ''}`,
          value: e.id,
        })),
      }),
    [availableSharedExpressions],
  );

  const clonePixelRow = (row: PixelRow, fixedId?: string): PixelRow =>
    createPixelRow({
      ...row,
      id: fixedId,
      urlTemplate: resolveProxyBaseUrlPlaceholder(row.urlTemplate, proxyBaseUrl),
      extractions: row.extractions.map((extraction) =>
        createExtractionRow({
          ...extraction,
          id: undefined,
        }),
      ),
    });

  const addSharedPixelById = (pixelId: string) => {
    const selectedPixel = sharedPixelRows.find((pixel) => pixel.id === pixelId);
    if (!selectedPixel) {
      return;
    }

    if (isSingleMode) {
      onChange([clonePixelRow(selectedPixel, 'pixel-0')]);
      return;
    }

    onChange([...workingRows, clonePixelRow(selectedPixel)]);
  };

  /** Save a single pixel row to the shared library as a new reusable pixel. */
  const savePixelToLibrary = async (row: PixelRow) => {
    if (!onSavePixelToLibrary) return;
    const dto = rowsToDtoCustomPixels([row])[0];
    if (!dto) return;
    // Drop the transient row id so the backend mints a fresh library pixel id.
    const { id: _rowId, ...pixel } = dto;
    setSavingPixelId(row.id);
    setSavePixelError(null);
    setSavedPixelId(null);
    try {
      await onSavePixelToLibrary({ ...pixel, reusable: true, enabled: true, visibility: 'private' });
      setSavedPixelId(row.id);
    } catch (err) {
      setSavePixelError({ id: row.id, message: err instanceof Error ? err.message : 'Could not save to library.' });
    } finally {
      setSavingPixelId(null);
    }
  };

  const addExtraction = (pixelId: string) => {
    onChange(
      workingRows.map((row) =>
        row.id === pixelId ? { ...row, extractions: [...row.extractions, createExtractionRow()] } : row,
      ),
    );
  };

  const updateExtraction = (pixelId: string, extractionId: string, patch: Partial<PixelRow['extractions'][number]>) => {
    onChange(
      workingRows.map((row) => {
        if (row.id !== pixelId) return row;
        return {
          ...row,
          extractions: row.extractions.map((ext) => (ext.id === extractionId ? { ...ext, ...patch } : ext)),
        };
      }),
    );
  };

  const removeExtraction = (pixelId: string, extractionId: string) => {
    onChange(
      workingRows.map((row) =>
        row.id === pixelId ? { ...row, extractions: row.extractions.filter((ext) => ext.id !== extractionId) } : row,
      ),
    );
  };

  /**
   * "Apply" a field mapping row: collapse it to the summary view and, for outbound
   * (inject) pixels, auto-append a `{field}={field}` parameter to the URL template
   * when the field macro is not yet present.
   */
  const applyExtraction = (pixelId: string, extId: string) => {
    const extKey = `${pixelId}:${extId}`;
    setCollapsedExtractions((prev) => ({ ...prev, [extKey]: true }));

    const pixel = workingRows.find((r) => r.id === pixelId);
    if (!pixel?.injectPixel) return;
    const ext = pixel.extractions.find((e) => e.id === extId);
    const fieldName = ext?.field?.trim();
    if (!fieldName) return;

    const fieldMacro = `{${fieldName}}`;
    if (!pixel.urlTemplate.includes(fieldMacro)) {
      const sep = pixel.urlTemplate.includes('?') ? '&' : '?';
      updatePixel(pixelId, {
        urlTemplate: `${pixel.urlTemplate}${sep}${fieldName}=${fieldMacro}`,
      });
    }
  };

  return (
    <VStack gap={3} align="stretch">
      <HStack justify="space-between" align="center" wrap="wrap" gap={2}>
        <Text fontSize="sm" fontWeight="600" color="fg.default">
          Custom Pixel Configurations
        </Text>
        <HStack gap={2} wrap="nowrap" flexShrink={0}>
          <Select.Root
            size="xs"
            collection={sharedPixelCollection}
            value={[]}
            disabled={sharedPixelRows.length === 0}
            onValueChange={(details) => {
              const selectedId = details.value[0];
              if (!selectedId) return;
              addSharedPixelById(selectedId);
            }}
          >
            <Select.Trigger borderColor="border.default" minW="160px">
              <Select.ValueText
                placeholder={sharedPixelRows.length > 0 ? 'Attach Shared Pixel' : 'No Shared Pixels Available'}
              />
            </Select.Trigger>
            <Portal>
              <Select.Positioner>
                <Select.Content>
                  {sharedPixelCollection.items.map((item) => (
                    <Select.Item key={item.value} item={item}>
                      {item.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Positioner>
            </Portal>
          </Select.Root>
          {showTemplateButton ? (
            <Select.Root
              size="xs"
              collection={pixelTemplateCollection}
              value={[]}
              onValueChange={(details) => {
                const selected = details.value[0] as PixelTemplateId | 'custom' | undefined;
                if (!selected) return;
                addFromSelection(selected);
              }}
            >
              <Select.Trigger
                bg="accent.solid"
                color="white"
                borderColor="accent.solid"
                _hover={{ bg: 'accent.ink', borderColor: 'accent.ink' }}
                width="fit-content"
                minW="unset"
              >
                <Select.ValueText placeholder={isSingleMode ? '+ New Pixel' : '+ Add Pixel'} />
              </Select.Trigger>
              <Portal>
                <Select.Positioner>
                  <Select.Content>
                    {pixelTemplateCollection.items.map((item) => (
                      <Select.Item key={item.value} item={item}>
                        {item.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Portal>
            </Select.Root>
          ) : !isSingleMode ? (
            <Button size="xs" bg="accent.solid" color="white" _hover={{ bg: 'accent.ink' }} onClick={addPixel}>
              + Add Pixel
            </Button>
          ) : null}
        </HStack>
      </HStack>

      <ValidationSummary issues={issues} onFix={null} review={findingReview} />

      {workingRows.length === 0 && (
        <Box border="1px solid" borderColor="border.default" borderRadius="8px" p={3}>
          <Text fontSize="xs" color="fg.muted">
            No custom pixels configured.
          </Text>
        </Box>
      )}

      {workingRows.map((pixel, index) => (
        <Box
          key={pixel.id}
          id={rowAnchorId('pixels', pixel.name?.trim() || `pixel ${index + 1}`)}
          border="1px solid"
          borderColor="border.default"
          borderRadius="8px"
          p={3}
        >
          <Flex justify="space-between" align="center" mb={2}>
            <Text fontSize="sm" fontWeight="600" color="fg.title">
              Pixel {index + 1}
            </Text>
            {!isSingleMode ? (
              <HStack gap={2}>
                {onSavePixelToLibrary && (
                  <Button
                    size="xs"
                    variant="outline"
                    borderColor="border.default"
                    color="accent.solid"
                    disabled={!pixel.name.trim() || savingPixelId === pixel.id}
                    title={
                      !pixel.name.trim()
                        ? 'Name the pixel before saving it to the library'
                        : 'Save this pixel to the shared library'
                    }
                    onClick={() => savePixelToLibrary(pixel)}
                    aria-label={`Save pixel ${pixel.name || index + 1} to the shared library`}
                  >
                    {savingPixelId === pixel.id ? 'Saving…' : savedPixelId === pixel.id ? 'Saved ✓' : 'Save to library'}
                  </Button>
                )}
                <Button
                  size="xs"
                  variant="outline"
                  borderColor="border.default"
                  onClick={() => toggleCollapsed(pixel.id)}
                  aria-label={`${collapsedRows[pixel.id] ? 'Expand' : 'Collapse'} pixel ${pixel.name || index + 1}`}
                >
                  {collapsedRows[pixel.id] ? 'Expand' : 'Collapse'}
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  borderColor="border.default"
                  onClick={() => removePixel(pixel.id)}
                  aria-label={`Remove pixel ${pixel.name || index + 1}`}
                >
                  Remove
                </Button>
              </HStack>
            ) : null}
          </Flex>
          {savePixelError?.id === pixel.id && (
            <Text fontSize="xs" color="error.ink" mb={1}>
              {savePixelError.message}
            </Text>
          )}

          {collapsedRows[pixel.id] ? (
            <Box bg="bg.canvas" borderRadius="6px" p={2}>
              <Text fontSize="xs" color="fg.muted" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                {pixel.name || 'Untitled'} ({pixel.type})
              </Text>
              <Text fontSize="xs" color="fg.muted" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                {pixel.urlTemplate || 'No URL template set'}
              </Text>
              <Text fontSize="xs" color="fg.muted">
                {pixel.extractions.length} extraction{pixel.extractions.length === 1 ? '' : 's'}
              </Text>
            </Box>
          ) : (
            <VStack gap={2} align="stretch">
              <HStack gap={2} align="flex-start">
                <Box flex="1">
                  <Text fontSize="xs" color="fg.muted" mb={1}>
                    Name
                  </Text>
                  <Input
                    size="sm"
                    value={pixel.name}
                    placeholder="e.g., Pricing Pixel"
                    onChange={(e) => updatePixel(pixel.id, { name: e.target.value })}
                    borderColor="border.default"
                    _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)' }}
                  />
                </Box>
                <Box minW="150px">
                  <Text fontSize="xs" color="fg.muted" mb={1}>
                    Type
                  </Text>
                  <Select.Root
                    size="sm"
                    collection={pixelTypeCollection}
                    value={[pixel.type]}
                    onValueChange={(details) => updatePixel(pixel.id, { type: details.value[0] as PixelRow['type'] })}
                  >
                    <Select.Trigger borderColor="border.default">
                      <Select.ValueText />
                    </Select.Trigger>
                    <Portal>
                      <Select.Positioner>
                        <Select.Content>
                          {pixelTypeCollection.items.map((item) => (
                            <Select.Item key={item.value} item={item}>
                              {item.label}
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Positioner>
                    </Portal>
                  </Select.Root>
                </Box>
                <Box minW="160px" pt={6}>
                  <Checkbox.Root
                    checked={pixel.injectPixel}
                    onCheckedChange={(details) => updatePixel(pixel.id, { injectPixel: !!details.checked })}
                    size="sm"
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control borderColor="accent.solid" />
                    <Checkbox.Label>
                      <Text fontSize="sm" color="fg.default">
                        Inject into VAST
                      </Text>
                    </Checkbox.Label>
                  </Checkbox.Root>
                  <Text fontSize="10px" color="fg.muted" mt={1} maxW="180px">
                    Added to this source's ad responses so the player fires it. Off = extraction-only (read values from
                    fired pixels).
                  </Text>
                </Box>
              </HStack>

              <Box>
                <HStack gap={2} mb={1} align="center">
                  <Text fontSize="xs" color="fg.muted">
                    URL Template
                  </Text>
                  {(() => {
                    const sourceRow = sourceRowsById.get(pixel.id);
                    const wasResolved = !!proxyBaseUrl && !!sourceRow?.urlTemplate?.includes('{{PROXY_BASE_URL}}');
                    return wasResolved ? (
                      <Badge size="sm" bg="info.tint" color="info.ink">
                        Resolved from config
                      </Badge>
                    ) : null;
                  })()}
                </HStack>
                <Textarea
                  size="sm"
                  rows={2}
                  value={pixel.urlTemplate}
                  placeholder="{{PROXY_BASE_URL}}/pixel?pid=pricing&sid={sessionId}&event=impression&cpm={bid}&cb={cb}"
                  onChange={(e) => updatePixel(pixel.id, { urlTemplate: e.target.value })}
                  borderColor="border.default"
                  _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)' }}
                  fontFamily="mono"
                  fontSize="xs"
                />
              </Box>

              <HStack gap={2} align="flex-start">
                <Box flex="1">
                  <Text fontSize="xs" color="fg.muted" mb={1}>
                    Description
                  </Text>
                  <Input
                    size="sm"
                    value={pixel.description}
                    placeholder="Optional description"
                    onChange={(e) => updatePixel(pixel.id, { description: e.target.value })}
                    borderColor="border.default"
                    _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)' }}
                  />
                </Box>
                <Box minW="180px">
                  <Text fontSize="xs" color="fg.muted" mb={1}>
                    Ad Server Template
                  </Text>
                  <Select.Root
                    size="sm"
                    collection={adServerTemplateCollection}
                    value={pixel.adServerType ? [pixel.adServerType] : []}
                    onValueChange={(details) =>
                      updatePixel(pixel.id, {
                        adServerType: details.value[0] ?? '',
                      })
                    }
                  >
                    <Select.Trigger borderColor="border.default">
                      <Select.ValueText placeholder="Choose template" />
                    </Select.Trigger>
                    <Portal>
                      <Select.Positioner>
                        <Select.Content>
                          {adServerTemplateCollection.items.map((item) => (
                            <Select.Item key={item.value} item={item}>
                              {item.label}
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Positioner>
                    </Portal>
                  </Select.Root>
                </Box>
                <Box flex="1">
                  <Text fontSize="xs" color="fg.muted" mb={1}>
                    Ad Server Type
                  </Text>
                  <Input
                    size="sm"
                    value={pixel.adServerType}
                    placeholder="Select template or enter custom"
                    onChange={(e) => updatePixel(pixel.id, { adServerType: e.target.value })}
                    borderColor="border.default"
                    _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)' }}
                  />
                </Box>
              </HStack>

              <Box borderTop="1px solid" borderColor="border.subtle" pt={2}>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="xs" fontWeight="600" color="fg.default">
                    Field Mappings
                  </Text>
                  <Button
                    size="xs"
                    variant="outline"
                    borderColor="border.default"
                    onClick={() => addExtraction(pixel.id)}
                  >
                    + Add Mapping
                  </Button>
                </HStack>
                <Text fontSize="xs" color="fg.muted" mb={2}>
                  Define how fields are mapped: for <strong>inbound</strong> pixels, use a regex Pattern to extract
                  values from the pixel URL; for <strong>outbound</strong> (injected) pixels, use an Expression to
                  populate URL parameters from request context. Click <strong>Apply</strong> on each row to confirm —
                  outbound pixels will have the field macro added to the URL template automatically.
                </Text>

                {pixel.extractions.length === 0 && (
                  <Text fontSize="xs" color="fg.muted">
                    No field mappings defined.
                  </Text>
                )}

                <VStack gap={2} align="stretch">
                  {pixel.extractions.map((ext) => {
                    const exprKey = `${pixel.id}:${ext.id}`;
                    const isExprExpanded = expandedExprKey === exprKey;
                    const isExtCollapsed = !!collapsedExtractions[exprKey];
                    const hasExpression = !!ext.mappingExpression;
                    const expressionValueType = inferValueTypeFromExpression(ext.mappingExpression);
                    const expressionDefaultValue = inferDefaultValueFromExpression(ext.mappingExpression);
                    const effectiveValueType = hasExpression
                      ? (expressionValueType ?? 'string')
                      : (ext.valueType ?? 'string');
                    const effectiveDefaultValue = hasExpression ? expressionDefaultValue : ext.defaultValue;
                    const fieldSuggestions = buildExtractionFieldSuggestions(pixel.urlTemplate);
                    const mappingSummary = hasExpression
                      ? summarizeMacroExpression(ext.mappingExpression, expressionNameFor)
                      : ext.pattern || '(no mapping)';
                    return (
                      <Box
                        key={ext.id}
                        bg="bg.canvas"
                        borderRadius="6px"
                        p={2}
                        border="1px solid"
                        borderColor="border.subtle"
                      >
                        {isExtCollapsed ? (
                          /* ── Collapsed summary row ── */
                          <HStack gap={2} justify="space-between" align="center">
                            <HStack gap={2} overflow="hidden" flex="1">
                              <Box
                                bg="accent.solid"
                                color="white"
                                borderRadius="4px"
                                px={1.5}
                                py={0.5}
                                fontSize="10px"
                                fontWeight="700"
                                lineHeight="1.4"
                                flexShrink={0}
                              >
                                {ext.field || '(field)'}
                              </Box>
                              <Text
                                fontSize="xs"
                                color="fg.muted"
                                whiteSpace="nowrap"
                                overflow="hidden"
                                textOverflow="ellipsis"
                                fontFamily={hasExpression ? undefined : 'mono'}
                              >
                                {mappingSummary}
                              </Text>
                              {effectiveValueType && (
                                <Box
                                  bg="bg.subtle"
                                  color="fg.muted"
                                  borderRadius="4px"
                                  px={1.5}
                                  py={0.5}
                                  fontSize="10px"
                                  lineHeight="1.4"
                                  flexShrink={0}
                                >
                                  {effectiveValueType}
                                </Box>
                              )}
                            </HStack>
                            <Button
                              size="xs"
                              variant="outline"
                              borderColor="border.default"
                              onClick={() => setCollapsedExtractions((prev) => ({ ...prev, [exprKey]: false }))}
                            >
                              Edit
                            </Button>
                          </HStack>
                        ) : (
                          /* ── Expanded edit view ── */
                          <>
                            <HStack gap={2} align="flex-start">
                              <Box flex="1">
                                <Text fontSize="xs" color="fg.muted" mb={1}>
                                  Field
                                </Text>
                                <MappingAutocompleteInput
                                  value={ext.field}
                                  onChange={(field) => updateExtraction(pixel.id, ext.id, { field })}
                                  placeholder="e.g., bid"
                                  size="sm"
                                  suggestions={fieldSuggestions}
                                />
                              </Box>
                              <Box flex="2">
                                <Text fontSize="xs" color="fg.muted" mb={1}>
                                  Primary Mapping
                                </Text>
                                {hasExpression ? (
                                  <Box
                                    border="1px solid"
                                    borderColor="accent.subtle"
                                    bg="bg.subtle"
                                    borderRadius="6px"
                                    p={2}
                                    cursor="pointer"
                                    tabIndex={0}
                                    role="button"
                                    onClick={() => setExpandedExprKey(exprKey)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        // Keep Enter here — the wizard advances a phase
                                        // on any stray Enter, so a toggle that let it
                                        // bubble both opened the panel and moved on.
                                        e.stopPropagation();
                                        setExpandedExprKey(exprKey);
                                      }
                                    }}
                                    title="Click to edit expression"
                                    aria-label={`Edit expression for extraction field ${ext.field || 'row'}`}
                                  >
                                    <HStack gap={2} align="center" wrap="nowrap">
                                      <Box
                                        display="inline-block"
                                        bg={expressionTypeBadgeColor(ext.mappingExpression!.type)}
                                        color="white"
                                        borderRadius="4px"
                                        px={1.5}
                                        py={0.5}
                                        fontSize="10px"
                                        fontWeight="700"
                                        lineHeight="1"
                                        flexShrink={0}
                                      >
                                        {expressionTypeLabel(ext.mappingExpression!.type)}
                                      </Box>
                                      <Text
                                        fontSize="xs"
                                        color="fg.muted"
                                        whiteSpace="nowrap"
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                      >
                                        {summarizeMacroExpression(ext.mappingExpression, expressionNameFor)}
                                      </Text>
                                    </HStack>
                                    {(() => {
                                      const description =
                                        ext.mappingExpression && 'description' in ext.mappingExpression
                                          ? ext.mappingExpression.description
                                          : undefined;
                                      return (
                                        description && (
                                          <Text
                                            fontSize="10px"
                                            color="fg.muted"
                                            mt={1}
                                            whiteSpace="nowrap"
                                            overflow="hidden"
                                            textOverflow="ellipsis"
                                            fontStyle="italic"
                                          >
                                            {description}
                                          </Text>
                                        )
                                      );
                                    })()}
                                  </Box>
                                ) : (
                                  <Input
                                    size="sm"
                                    value={ext.pattern}
                                    placeholder="e.g., cpm=([0-9.]+)"
                                    onChange={(e) => updateExtraction(pixel.id, ext.id, { pattern: e.target.value })}
                                    bg="bg.card"
                                    borderColor="border.default"
                                    _focus={{
                                      borderColor: 'accent.solid',
                                      boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)',
                                    }}
                                    fontFamily="mono"
                                    fontSize="xs"
                                  />
                                )}
                              </Box>
                              <Box minW="120px">
                                <Text fontSize="xs" color="fg.muted" mb={1}>
                                  Expression
                                </Text>
                                <ExpressionBadge
                                  expression={ext.mappingExpression}
                                  onEdit={() => setExpandedExprKey(isExprExpanded ? null : exprKey)}
                                  onClear={
                                    ext.mappingExpression
                                      ? () =>
                                          updateExtraction(pixel.id, ext.id, {
                                            mappingExpression: undefined,
                                            // Reset inferred fields so the user starts from a
                                            // clean slate rather than seeing expression-derived
                                            // values as if they were manually set.
                                            valueType: undefined,
                                            defaultValue: undefined,
                                          })
                                      : undefined
                                  }
                                  size="xs"
                                />
                              </Box>
                              <Button
                                size="xs"
                                variant="outline"
                                borderColor="border.default"
                                onClick={() => removeExtraction(pixel.id, ext.id)}
                                aria-label={`Remove extraction for field ${ext.field || 'row'}`}
                              >
                                Remove
                              </Button>
                            </HStack>
                            <HStack gap={2} mt={2} align="flex-start">
                              <Box minW="130px">
                                <Text fontSize="xs" color="fg.muted" mb={1}>
                                  Value Type
                                </Text>
                                <Select.Root
                                  size="sm"
                                  collection={valueTypeCollection}
                                  disabled={hasExpression}
                                  value={[effectiveValueType]}
                                  onValueChange={(details) =>
                                    updateExtraction(pixel.id, ext.id, {
                                      valueType: details.value[0] as PixelExtractionRow['valueType'],
                                    })
                                  }
                                >
                                  <Select.Trigger borderColor="border.default" bg="bg.card">
                                    <Select.ValueText />
                                  </Select.Trigger>
                                  <Portal>
                                    <Select.Positioner>
                                      <Select.Content>
                                        {valueTypeCollection.items.map((item) => (
                                          <Select.Item key={item.value} item={item}>
                                            {item.label}
                                          </Select.Item>
                                        ))}
                                      </Select.Content>
                                    </Select.Positioner>
                                  </Portal>
                                </Select.Root>
                              </Box>
                              <Box minW="130px">
                                <Text fontSize="xs" color="fg.muted" mb={1}>
                                  Transform
                                </Text>
                                <Select.Root
                                  size="sm"
                                  collection={transformCollection}
                                  disabled={hasExpression}
                                  value={[ext.transform ?? '']}
                                  onValueChange={(details) =>
                                    updateExtraction(pixel.id, ext.id, {
                                      transform: (details.value[0] || undefined) as PixelExtractionRow['transform'],
                                    })
                                  }
                                >
                                  <Select.Trigger borderColor="border.default" bg="bg.card">
                                    <Select.ValueText />
                                  </Select.Trigger>
                                  <Portal>
                                    <Select.Positioner>
                                      <Select.Content>
                                        {transformCollection.items.map((item) => (
                                          <Select.Item key={item.value || 'none'} item={item}>
                                            {item.label}
                                          </Select.Item>
                                        ))}
                                      </Select.Content>
                                    </Select.Positioner>
                                  </Portal>
                                </Select.Root>
                              </Box>
                              <Box minW="120px">
                                <Text fontSize="xs" color="fg.muted" mb={1}>
                                  Default Value
                                </Text>
                                <Input
                                  size="sm"
                                  disabled={hasExpression}
                                  value={effectiveDefaultValue == null ? '' : String(effectiveDefaultValue)}
                                  placeholder={hasExpression ? 'Set default in expression editor' : undefined}
                                  onChange={(e) =>
                                    updateExtraction(pixel.id, ext.id, {
                                      defaultValue: e.target.value ? e.target.value : undefined,
                                    })
                                  }
                                  bg="bg.card"
                                  borderColor="border.default"
                                  _focus={{
                                    borderColor: 'accent.solid',
                                    boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)',
                                  }}
                                />
                              </Box>
                              <Box flex="1">
                                <Text fontSize="xs" color="fg.muted" mb={1}>
                                  Description
                                </Text>
                                <Input
                                  size="sm"
                                  value={ext.description ?? ''}
                                  onChange={(e) => updateExtraction(pixel.id, ext.id, { description: e.target.value })}
                                  bg="bg.card"
                                  borderColor="border.default"
                                  _focus={{
                                    borderColor: 'accent.solid',
                                    boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)',
                                  }}
                                />
                              </Box>
                            </HStack>
                            {isExprExpanded && (
                              <Box
                                mt={2}
                                border="1px solid"
                                borderColor="accent.subtle"
                                borderRadius="8px"
                                p={3}
                                bg="bg.card"
                              >
                                <HStack justify="space-between" mb={2}>
                                  <Text fontSize="xs" fontWeight="600" color="accent.solid">
                                    Decorator Expression — {ext.field || 'mapping field'}
                                  </Text>
                                  <HStack gap={2}>
                                    {availableSharedExpressions.length > 0 && (
                                      <Select.Root
                                        size="xs"
                                        collection={sharedExpressionCollection}
                                        value={[]}
                                        onValueChange={(d) => {
                                          const selected = availableSharedExpressions.find((e) => e.id === d.value[0]);
                                          if (selected) {
                                            updateExtraction(pixel.id, ext.id, {
                                              mappingExpression: selected.expression,
                                              valueType: inferValueTypeFromExpression(selected.expression),
                                              defaultValue: inferDefaultValueFromExpression(selected.expression),
                                            });
                                          }
                                        }}
                                      >
                                        <Select.Trigger borderColor="accent.solid" color="accent.solid" minW="160px">
                                          <Select.ValueText placeholder="📂 Apply from library" />
                                        </Select.Trigger>
                                        <Portal>
                                          <Select.Positioner>
                                            <Select.Content>
                                              {sharedExpressionCollection.items.map((item) => (
                                                <Select.Item key={item.value} item={item}>
                                                  {item.label}
                                                </Select.Item>
                                              ))}
                                            </Select.Content>
                                          </Select.Positioner>
                                        </Portal>
                                      </Select.Root>
                                    )}
                                    <Button
                                      size="xs"
                                      variant="outline"
                                      borderColor="border.default"
                                      onClick={() => setExpandedExprKey(null)}
                                    >
                                      Close
                                    </Button>
                                  </HStack>
                                </HStack>
                                <DecoratorExpressionEditor
                                  // Outside this pass (`#886`), and a pixel URL's empty-value rule is not measured.
                                  emptyValue={null}
                                  // The pixel editor is outside this pass (`#886`), and a pixel is a URL whose values are text.
                                  authorsYield={null}
                                  expression={
                                    ext.mappingExpression ?? ({ type: 'calculated', path: '' } as MacroExpression)
                                  }
                                  onChange={(expr) =>
                                    updateExtraction(pixel.id, ext.id, {
                                      mappingExpression: expr,
                                      valueType: inferValueTypeFromExpression(expr),
                                      defaultValue: inferDefaultValueFromExpression(expr),
                                    })
                                  }
                                  onSaveToLibrary={onSaveExpressionToLibrary ?? null}
                                  /* This editor renders no chip row of its own — it is rebuilt by `XS3` slice 8 rather
                                     than converted — so the shape control comes from inside. */
                                  shapeControl="own"
                                  /* A branch here may address a shared expression like any other
                                     (`E4`, `#1272`); nothing about a pixel argues otherwise. */
                                  offersLibrary={true}
                                  /* **`null` is the answer, and it is a real one.** No entry in
                                     `RESOLVE_CONTEXT_BUILDERS` declares the context a pixel's
                                     extraction resolves against, so naming one here would be a guess —
                                     and the one output this must never produce is an expression marked
                                     unusable where it resolves perfectly well (`#1002`). With no
                                     context named, no claim is made and nothing is marked. */
                                  resolvesAgainst={null}
                                />
                              </Box>
                            )}
                            <HStack justify="flex-end" mt={2}>
                              <Button
                                size="xs"
                                bg="accent.solid"
                                color="white"
                                _hover={{ bg: 'accent.ink' }}
                                onClick={() => applyExtraction(pixel.id, ext.id)}
                                title={
                                  pixel.injectPixel
                                    ? 'Apply mapping and add field macro to URL template'
                                    : 'Apply mapping and collapse row'
                                }
                              >
                                Apply
                              </Button>
                            </HStack>
                          </>
                        )}
                      </Box>
                    );
                  })}
                </VStack>
              </Box>
            </VStack>
          )}
        </Box>
      ))}
    </VStack>
  );
};
